import React, { useRef, useState } from 'react';

export interface BlockReason {
  agent: string;
  role?: string;
  layer: string;
  reason: string;
  fix: string;
}

const POPOVER_WIDTH = 380;

/** Wraps a status badge. On hover (or tap) shows why the assistant is
 *  blocked and how to fix it. Uses fixed positioning so scrolling table
 *  containers don't clip it. */
export default function BlockReasonPopover({
  impact,
  reasons,
  children,
}: {
  impact?: string | null;
  reasons?: BlockReason[] | null;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);

  if (!reasons || reasons.length === 0) return <>{children}</>;

  const show = () => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 12));
    const above = rect.bottom > window.innerHeight * 0.6;
    setPos({ top: above ? rect.top - 8 : rect.bottom + 8, left, above });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      className="inline-block cursor-help"
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={(e) => {
        e.stopPropagation();
        pos ? hide() : show();
      }}
    >
      {children}
      {pos && (
        <span
          className="fixed z-[100] block bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-left text-xs text-gray-700 whitespace-normal font-normal"
          style={{
            top: pos.top,
            left: pos.left,
            width: POPOVER_WIDTH,
            maxWidth: 'calc(100vw - 24px)',
            transform: pos.above ? 'translateY(-100%)' : undefined,
          }}
        >
          {impact && <span className="block font-semibold text-gray-900 mb-2">{impact}</span>}
          {reasons.map((r, i) => (
            <span key={`${r.agent}-${r.layer}-${i}`} className={`block ${i > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}`}>
              <span className="block">
                <span className="font-mono font-semibold text-gray-900">{r.agent}</span>
                {r.role && <span className="text-gray-500"> · {r.role}</span>}
              </span>
              <span className="block mt-1">
                <span className="font-semibold text-rose-700">Why: </span>
                {r.reason}
              </span>
              <span className="block mt-1 whitespace-pre-line">
                <span className="font-semibold text-emerald-700">How to fix: </span>
                {r.fix}
              </span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
